import React from 'react';
import classnames from 'classnames';
import { TextArea, TableColumn, TextAreaProps } from 'tea-component';
import EllipsisHover from '@gp/components/EllipsisHover';
import TinyEditor from '@gp/components/TinyEditor';
import BoolSelect from '@gp/components/BoolSelect';
import GPSelect from '@gp/components/Select';
import FollowerRange from '@gp/components/FollowerRange';
import AutoTextarea from '@gp/components/AutoTextarea';
import KeyUpdatesContents from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard/KeyUpdatesContents';
import { extractTextFromHtml } from '@home/utils/common';
import { isEmpty, formatDateStr } from '@gp/utils/common';
import { ProjectPriority } from '@gp/enums/project';
import { CONSTANT_TEXTS, PRIORITY_COLOR } from '@gp/constants/const';
import RestrictedContent from '@gp/components/RestrictedContent';
import TabExpandAll from '@gp/components/TabExpandAll';
import type { IRecord, ILastUpdateInfo } from '@gp/types/global';
import type { IProjectContactsDTO } from '@gp/types/project';

export type ICommonColumnActionType = 'add' | 'edit' | 'delete' | (string & {});

export type ICommonColumnChangeHandler<T = IRecord, K = string> = (
  cvm: T,
  key: string,
  payload: K,
  rowKey?: string,
  rowIndex?: number,
) => void;

export type ICommonColumnClearHandler<T = IRecord> = (cvm: T, key: string, rowKey?: string, rowIndex?: number) => void;

const DEFAULT_COLUMN_ACTIONS: ICommonColumnActionType[] = ['edit'];

const ACTION_NAME_MAPPING: Record<ICommonColumnActionType, string> = {
  add: CONSTANT_TEXTS.ADD,
  edit: CONSTANT_TEXTS.EDIT,
  delete: CONSTANT_TEXTS.DELETE,
};

const PureTextColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    formatter?: (payload: T) => any;
    placeholder?: string;
    permissionKey?: string[];
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    header,
    formatter,
    placeholder = '-',
    permissionKey,
    handleExpandAll,
    isExpandTab = false,
    ...restProps
  } = props || {};
  return {
    ...restProps,
    key,
    align,
    header,
    render: (cvm: any) => {
      const { project_auth: projectAuth } = cvm || ({} as any);
      if (projectAuth === false) return <RestrictedContent />;
      if (Array.isArray(permissionKey) && permissionKey?.includes('tab_auth')) {
        const authKeyTab = `tab_auth_${cvm?.tab}`;
        if ((cvm as any)?.[authKeyTab] === false) return <RestrictedContent />;
      }
      if (Array.isArray(permissionKey) && permissionKey?.includes('field_auth')) {
        const authKeyField = `field_auth_${cvm?.field}`;
        if ((cvm as any)?.[authKeyField] === false) return <RestrictedContent />;
      }

      if (cvm?.tab === 'ExpandAll' && isExpandTab) {
        return <TabExpandAll data={cvm} totalNum={cvm?.filesNum} handleExpandAll={handleExpandAll} />;
      }

      const value = typeof formatter === 'function' ? formatter(cvm) : (cvm as any)?.[key];
      return isEmpty(value) ? placeholder : value;
    },
  };
};

const RichTextColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    placeholder?: string;
  },
) => TableColumn<T> = (props) => {
  const { key, align = 'left', placeholder = '-', ...restProps } = props;
  return {
    ...restProps,
    key,
    align,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      const showRichText = !!extractTextFromHtml(value)?.trim();
      if (!showRichText) return <span>{placeholder}</span>;
      return <KeyUpdatesContents data={value} />;
    },
  };
};

const LineWrapperTextColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    placeholder?: string;
  },
) => TableColumn<T> = (props) => {
  const { key, align = 'left', placeholder = '-', ...restProps } = props;
  return {
    ...restProps,
    key,
    align,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      const showLineWrapperText = !!value?.trim();
      if (!showLineWrapperText) return <span>{placeholder}</span>;
      return <AutoTextarea className="dbg-autotextarea--transparent" readOnly defaultValue={value} />;
    },
  };
};

const BoolSelectColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    label?: string;
    placeholder?: string;
    button?: (cvm: T) => string | React.ReactElement;
    disabled?: boolean;
    getIsDisabled?: (cvm: T) => TextAreaProps['disabled'];
    onChange?: ICommonColumnChangeHandler<T>;
    handleExpandAll?: ICommonColumnChangeHandler<T>;
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    label = '',
    placeholder = label ? `Please select ${label}` : 'Please select',
    button,
    disabled: disabledProps,
    getIsDisabled,
    onChange,
    handleExpandAll,
    ...restProps
  } = props;

  return {
    ...restProps,
    key,
    align,
    width: 120,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      const disabled = disabledProps ?? getIsDisabled?.(cvm) ?? false;
      return (
        <BoolSelect
          style={{ width: 86 }}
          placeholder={placeholder}
          button={button?.(cvm)}
          disabled={disabled}
          value={value}
          onChange={(payload) => {
            onChange?.(cvm, key, payload);
          }}
        />
      );
    },
  };
};

const SelectColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    label?: string;
    placeholder?: string;
    clearable?: boolean;
    options: IGPSelectOptionWithGroup[];
    onChange?: ICommonColumnChangeHandler<T>;
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    label = '',
    placeholder = label ? `Please select ${label}` : 'Please select',
    clearable = false,
    options,
    onChange,
    ...restProps
  } = props;
  return {
    ...restProps,
    key,
    align,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      return (
        <GPSelect
          placeholder={placeholder}
          clearable={clearable}
          options={options}
          value={value}
          onChange={(payload) => {
            onChange?.(cvm, key, payload);
          }}
        />
      );
    },
  };
};

const CustomizeSelectColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    className?: string;
    boxClassName?: string;
    dropdownOverlayBoxClassName?: string;
    label?: string;
    button?: (cvm: T, rowKey: string, rowIndex: number) => string | React.ReactElement | null;
    placeholder?: string;
    customizePlaceholder?: string;
    disabled?: boolean;
    getIsDisabled?: (cvm: T) => boolean;
    matchButtonWidth?: boolean;
    searchable?: boolean;
    clearable?: boolean;
    onClear?: ICommonColumnClearHandler<T>;
    options: IGPSelectOptionWithGroup[];
    onChange?: ICommonColumnChangeHandler<T>;
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    className,
    boxClassName,
    dropdownOverlayBoxClassName,
    label = '',
    button,
    placeholder = label ? `Please select ${label}` : 'Please select',
    customizePlaceholder = label ? `Please input ${label}` : 'Please input customize name',
    disabled: disabledProps,
    getIsDisabled,
    matchButtonWidth = false,
    searchable = true,
    clearable = false,
    onClear,
    options,
    onChange,
    ...restProps
  } = props;

  return {
    ...restProps,
    key,
    align,
    render: (cvm, rowKey, rowIndex) => {
      const value = (cvm as any)?.[key];
      const disabled = disabledProps ?? getIsDisabled?.(cvm) ?? false;
      return (
        <FollowerRange
          className={className}
          boxClassName={boxClassName}
          dropdownOverlayBoxClassName={dropdownOverlayBoxClassName}
          label={label}
          button={button?.(cvm, rowKey, rowIndex)}
          placeholder={placeholder}
          customizePlaceholder={customizePlaceholder}
          disabled={disabled}
          matchButtonWidth={matchButtonWidth}
          searchable={searchable}
          clearable={clearable}
          onClear={(e: React.SyntheticEvent) => {
            e?.stopPropagation();
            onClear?.(cvm, key, rowKey, rowIndex);
          }}
          options={options}
          value={value}
          onChange={(payload: string) => {
            onChange?.(cvm, key, payload, rowKey, rowIndex);
          }}
        />
      );
    },
  };
};

const TextareaColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    label?: string;
    placeholder?: string;
    size?: TextAreaProps['size'];
    rows?: TextAreaProps['rows'];
    disabled?: TextAreaProps['disabled'];
    getIsDisabled?: (cvm: T) => TextAreaProps['disabled'];
    onChange?: ICommonColumnChangeHandler<T>;
    isCalendar?: boolean;
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    label = '',
    placeholder = label ? `Please input ${label}` : 'Please input',
    size = 'full',
    rows = 2,
    disabled: disabledProps,
    getIsDisabled,
    onChange,
    isCalendar = false,
    ...restProps
  } = props;

  return {
    ...restProps,
    key,
    align,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      const disabled = disabledProps ?? getIsDisabled?.(cvm) ?? false;
      return (
        <TextArea
          className={classnames('dbg-textarea', {
            'dbg-textarea-is-calendar': isCalendar,
          })}
          placeholder={placeholder}
          size={size}
          rows={rows}
          disabled={disabled}
          value={value}
          onChange={(payload) => {
            onChange?.(cvm, key, payload);
          }}
        />
      );
    },
  };
};

const EditorColumnBuilder: <T = IRecord>(
  props: TableColumn<T> & {
    label?: string;
    placeholder?: string;
    minHeight?: number;
    status?: 'success' | 'error';
    readOnly?: boolean;
    disabled?: boolean;
    getIsDisabled?: (cvm: T) => boolean;
    onChange?: ICommonColumnChangeHandler<T>;
  },
) => TableColumn<T> = (props) => {
  const {
    key,
    align = 'left',
    label = '',
    placeholder = label ? `Please input ${label}` : 'Please input',
    minHeight,
    status = 'success',
    readOnly = false,
    disabled: disabledProps,
    getIsDisabled,
    onChange,
    ...restProps
  } = props;

  return {
    ...restProps,
    key,
    align,
    render: (cvm) => {
      const value = (cvm as any)?.[key];
      const disabled = disabledProps ?? getIsDisabled?.(cvm) ?? false;
      return (
        <TinyEditor
          synchronize
          className={classnames('dbg-common-editor gp-word-break-word', {
            'dbg-common-editor--disabled': disabled,
          })}
          placeholder={placeholder}
          minHeight={minHeight}
          status={status}
          readOnly={readOnly || disabled}
          value={value}
          getEditorContent={(payload) => {
            onChange?.(cvm, key, payload);
          }}
          getEditorText={() => {}}
          getIsFocused={() => {}}
        />
      );
    },
  };
};

const LastUpdateColumnBuilder: <T extends ILastUpdateInfo>(
  props?: Omit<TableColumn<T>, 'key' | 'header'> & {
    key?: string;
    header?: string | React.ReactElement;
    updateTimeFormatter?: (payload: string) => string;
    placeholder?: string;
    permissionKey?: string[];
  },
) => TableColumn<T> = (props) => {
  const {
    key = 'update_time',
    align = 'left',
    header = 'Last Update',
    width = 160,
    updateTimeFormatter = formatDateStr,
    placeholder = '-',
    permissionKey,
    ...restProps
  } = props || {};
  return {
    ...restProps,
    key,
    align,
    header,
    width,
    render: (cvm) => {
      const { update_user: updateUser, update_time: updateTime, project_auth: projectAuth } = cvm || {};
      if (projectAuth === false) return <RestrictedContent />;
      if (Array.isArray(permissionKey) && permissionKey?.includes('tab_auth')) {
        const authKeyTab = `tab_auth_${cvm?.tab}`;
        if ((cvm as any)?.[authKeyTab] === false) return <RestrictedContent />;
      }
      if (Array.isArray(permissionKey) && permissionKey?.includes('field_auth')) {
        const authKeyField = `field_auth_${cvm?.field}`;
        if ((cvm as any)?.[authKeyField] === false) return <RestrictedContent />;
      }

      const updateTimeTmp = updateTimeFormatter?.(updateTime) || updateTime;
      if (!updateUser && !updateTimeTmp) return <div>{placeholder}</div>;
      if (!!updateUser && !!updateTimeTmp) {
        return (
          <div>
            <div className="dbg-table-line-height-22">{updateUser} on</div>
            <div>{updateTimeTmp}</div>
          </div>
        );
      }
      if (!!updateUser) return <div>{updateUser}</div>;
      return <div>{updateTimeTmp}</div>;
    },
  };
};

const ActionsColumnBuilder: <T = IRecord>(
  props?: Omit<TableColumn<T>, 'key' | 'header'> & {
    key?: string;
    header?: string | React.ReactElement;
    actions?: ICommonColumnActionType[];
    onActionHandler?: (action: ICommonColumnActionType, record: T) => void;
  },
) => TableColumn<T> = (props) => {
  const {
    key = 'actions',
    align = 'left',
    header = CONSTANT_TEXTS.ACTIONS,
    width = 94,
    actions = DEFAULT_COLUMN_ACTIONS,
    onActionHandler,
    ...restProps
  } = props || {};
  return {
    ...restProps,
    key,
    align,
    header,
    width,
    render: (cvm) => {
      if (!Array.isArray(actions) || actions.length === 0) return null;
      return (
        <ul className="dbg-actions dbg-actions__action-list">
          {actions?.map((action) => {
            return (
              <li key={action} className="dbg-actions__action-item">
                <a
                  className="dbg-actions__action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onActionHandler?.(action, cvm);
                  }}
                >
                  {ACTION_NAME_MAPPING[action] || action}
                </a>
              </li>
            );
          })}
        </ul>
      );
    },
  };
};

// 常用列构建器
export const CommonColumnBuilders = {
  // 纯文本column
  buildPureTextColumn: PureTextColumnBuilder,
  // 富文本column
  buildRichTextColumn: RichTextColumnBuilder,
  // 多行文本column
  buildLineWrapperTextColumn: LineWrapperTextColumnBuilder,
  // 富文本编辑组件column
  buildEditorColumn: EditorColumnBuilder,
  // BoolSelect组件column
  buildBoolSelectColumn: BoolSelectColumnBuilder,
  // GPSelect组件column
  buildSelectColumn: SelectColumnBuilder,
  // 自定义Select组件column
  buildCustomizeSelectColumn: CustomizeSelectColumnBuilder,
  // Textarea组件column
  buildTextareaColumn: TextareaColumnBuilder,
  // Last Update列column
  buildLastUpdateColumn: LastUpdateColumnBuilder,
  // Actions列column
  buildActionsColumn: ActionsColumnBuilder,
};

// Project的Priority列构造器
const ProjectPriorityColumnBuilder: <T extends { priority: string; }>(
  props?: TableColumn<T> & {
    placeholder?: string;
  },
) => TableColumn<T> = (props) => {
  const {
    key = 'priority',
    align = 'left',
    header = 'Priority',
    width = 116,
    placeholder = '-',
    ...restProps
  } = props || {};
  return {
    ...restProps,
    key,
    align,
    header,
    width,
    render: (cvm) => {
      const { priority } = cvm || {};
      // @ts-ignore
      const priorityName = ProjectPriority[priority];
      // @ts-ignore
      const priorityColor = PRIORITY_COLOR[priority];
      if (!priority || !priorityName) return <span>{placeholder}</span>;
      return (
        <div className="dbg-table-common-text">
          <div style={{ background: priorityColor }} className="dbg-table-common-line"></div>
          {priorityName}
        </div>
      );
    },
  };
};

// Project的Contact信息列构造器
const ProjectContactsColumnBuilder: <T extends IProjectContactsDTO>(
  props?: TableColumn<T> & {
    placeholder?: string;
  },
) => TableColumn<T> = (props) => {
  const { key = '', align = 'left', header, width = 130, placeholder = '-', ...restProps } = props || {};
  return {
    ...restProps,
    key,
    align,
    header,
    width,
    render: (cvm) => {
      // @ts-ignore
      const contacts = cvm?.[key] || [];
      if (!Array.isArray(contacts) || contacts.length === 0) return <span>{placeholder}</span>;
      return (
        <div className="dbg-pipeline-project-contacts-info-list">
          {contacts.map((contact, contactIndex) => {
            return (
              <div key={contactIndex} className="dbg-pipeline-project-contacts-info-item">
                <EllipsisHover text={contact} />
              </div>
            );
          })}
        </div>
      );
    },
  };
};

// Project相关的列构造器
export const ProjectColumnBuilders = {
  buildProjectPriorityColumn: ProjectPriorityColumnBuilder,
  buildProjectContactsColumn: ProjectContactsColumnBuilder,
};
